import axios from 'axios';

//const API_BASE = 'http://localhost:8080/api/v1';
const API_BASE = 'https://api.future-of-audio.com/api/v1';
const AUTH_BASE = 'https://auth.future-of-audio.com';

// ---------------------------------------------------------------------------
// Authentication (PocketBase `users` collection)
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'chronos_auth_token';
const USER_KEY = 'chronos_auth_user';
const USER_ID_KEY = 'chronos_auth_user_id';

export const getAuthToken = () => localStorage.getItem(TOKEN_KEY) || '';
export const setAuthToken = (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
};
export const clearAuthToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_ID_KEY);
};
export const isAuthenticated = () => !!getAuthToken();
export const getAuthUser = () => localStorage.getItem(USER_KEY) || '';
export const getAuthUserId = () => localStorage.getItem(USER_ID_KEY) || '';

// Attach the token to every axios request.
axios.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = token;
    return config;
});

// Drop the token on 401 so the UI can prompt for re-login.
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401) clearAuthToken();
        return Promise.reject(error);
    }
);

// Build headers for the raw fetch() calls, including the token when present.
const authHeaders = (extra = {}) => {
    const token = getAuthToken();
    return token ? { ...extra, Authorization: token } : extra;
};

// Log in against PocketBase and store the returned token.
export const login = async (identity, password) => {
    const response = await fetch(`${AUTH_BASE}/api/collections/users/auth-with-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identity, password }),
    });
    if (!response.ok) {
        throw new Error(`Login failed (${response.status})`);
    }
    const data = await response.json();
    setAuthToken(data.token);
    const displayName = data.record?.name || data.record?.email || identity;
    localStorage.setItem(USER_KEY, displayName);
    if (data.record?.id) localStorage.setItem(USER_ID_KEY, data.record.id);
    return data.record;
};

export const logout = () => clearAuthToken();

// Self-registration into the PocketBase `users` collection. The account is
// created with approved=false and cannot log in until an admin approves it.
export const register = async (email, password, name) => {
    const response = await fetch(`${AUTH_BASE}/api/collections/users/records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // emailVisibility:true is required by this instance's users collection and
        // also lets the account be found by email when adding it as a share grantee.
        body: JSON.stringify({ email, password, passwordConfirm: password, name, emailVisibility: true }),
    });
    if (!response.ok) {
        let message = `Registration failed (${response.status})`;
        try {
            const err = await response.json();
            // PocketBase returns a generic top-level "message" ("Failed to create
            // record.") plus per-field reasons in "data". Surface the field
            // details so the actual cause (e.g. password too short, email taken,
            // unknown field) is visible instead of the generic message.
            const fieldErrors = err?.data
                ? Object.entries(err.data)
                    .map(([field, info]) => `${field}: ${info?.message || info?.code || 'invalid'}`)
                    .join('; ')
                : '';
            if (fieldErrors) message = fieldErrors;
            else if (err?.message) message = err.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    return response.json();
};

export const fetchDevice = (deviceUuid) => axios.get(`${API_BASE}/devices/${deviceUuid}`);
export const fetchDevices = () => axios.get(`${API_BASE}/devices`);

// PocketBase device records for the authenticated user. A blob-path deviceId is
// the PocketBase device *record id*, whereas the service keys devices by their
// *username* (uuid). These records expose both, letting the UI bridge the two.
// The devices listRule ("owner = @request.auth.id") already scopes this to the
// caller, so only owned devices are returned.
export const fetchDeviceRecords = () =>
    fetch(`${AUTH_BASE}/api/collections/devices/records?perPage=500`, { headers: authHeaders() })
        .then((response) => (response.ok ? response.json() : { items: [] }))
        .catch(() => ({ items: [] }));
export const fetchFiles = (folderId) => axios.get(`${API_BASE}/files/${folderId}`);
export const fetchFileProperties = (filePath) => {
    const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
    return axios.get(`${API_BASE}/files/${encoded}?op=properties`);
};

export const setDeviceProperty = (deviceUuid, propertyName, propertyValue) =>
    {
        const url = `${API_BASE}/devices/${deviceUuid}?${propertyName}=${propertyValue}`;
        return axios.post(url);
    }

export const fetchFileChunk =
    (filePath, bytesFrom, bytesSize, signal) =>
    {
        const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
        const url = `${API_BASE}/files/${encoded}?bytesFrom=${bytesFrom}&bytesSize=${bytesSize}`;
        return fetch(url, { signal, headers: authHeaders() });
    };

export const fetchFileComplete =
    (filePath, signal) =>
    {
        const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
        const url = `${API_BASE}/files/${encoded}`;
        return fetch(url, { signal, headers: authHeaders() });
    };

// Bytes pulled per range request. The service buffers each response fully in RAM
// (served has no streaming response API), so a single whole-file GET of a large
// recording OOM-kills it. Downloading in bounded ranges caps the server's
// per-request allocation to this size.
export const DOWNLOAD_CHUNK_SIZE = 8 * 1024 * 1024; // 8 MiB

// Stream a blob from the service in sequential range requests and expose the
// result as a standard Response backed by a ReadableStream. This is a drop-in
// for fetchFileComplete for streaming consumers (e.g. the BW64 export), but the
// server never has to hold more than one chunk at a time. The stream is lazy:
// no network request is issued until the body is read, so constructing it does
// not consume a user-activation window.
export const fetchFileStreamed =
    (filePath, totalSize, signal) =>
    {
        let offset = 0;
        const stream = new ReadableStream({
            async pull(controller)
            {
                if ((signal && signal.aborted) || (offset >= totalSize))
                {
                    controller.close();
                    return;
                }
                const size = Math.min(DOWNLOAD_CHUNK_SIZE, totalSize - offset);
                const response = await fetchFileChunk(filePath, offset, size, signal);
                if (!response.ok)
                {
                    controller.error(new Error(`Server returned ${response.status}`));
                    return;
                }
                const chunk = new Uint8Array(await response.arrayBuffer());
                if (chunk.length === 0)
                {
                    controller.close();
                    return;
                }
                controller.enqueue(chunk);
                offset += chunk.length;
                if ((chunk.length < size) || (offset >= totalSize))
                {
                    controller.close();
                }
            }
        });
        return new Response(stream, {
            status: 200,
            headers: {
                'Content-Length': String(totalSize),
                'Content-Type': 'application/octet-stream'
            }
        });
    };

export const apiGetComments = (filePath) => {
    const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
    return axios.get(`${API_BASE}/files/${encoded}?op=comments`);
};

export const apiGetLocations = (filePath) => {
    const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
    // JSON-Lines payload (one breadcrumb per line) - keep it as raw text so axios
    // does not try to JSON.parse the whole body.
    return axios.get(`${API_BASE}/files/${encoded}?op=locations`, { responseType: 'text' });
};

export const apiAddComment = (filePath, comment) =>
    {
        const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
        const url = `${API_BASE}/files/${encoded}?op=addComment`;
        return axios.post(url, comment);
    }

export const apiDeleteComment = (filePath, commentUuid) =>
    {
        const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
        const url = `${API_BASE}/files/${encoded}?op=deleteComment`;
        return axios.post(url, { uuid: commentUuid });
    }

// ---------------------------------------------------------------------------
// Recording label (a user-editable name for the whole take)
// ---------------------------------------------------------------------------
// A "label" is stored as a special entry in the same "-comments.json" sidecar,
// distinguished by having NO timeFrom (real comments always carry a timeline
// position). Reusing the comment infrastructure means zero backend changes:
// add/delete are already auth-gated, broadcast a "comments updated" push, and
// the sidecar is purged with the recording.

// Current label text for a recording ('' when none). If several labels somehow
// exist, the most recently added one wins (last-write-wins).
export const getFileLabel = async (filePath) => {
    try {
        const res = await apiGetComments(filePath);
        if (!res || res.status !== 200 || !Array.isArray(res.data)) return '';
        const labels = res.data.filter((c) => c && !c.timeFrom);
        if (labels.length === 0) return '';
        return labels[labels.length - 1].text || '';
    } catch {
        return '';
    }
};

// Replace the recording's label. Deletes any existing label(s) first — awaited
// so the single-threaded server queue stays FIFO-ordered and never accumulates
// duplicates — then adds the new one. An empty/blank text simply clears it.
export const setFileLabel = async (filePath, text) => {
    let existing = [];
    try {
        const res = await apiGetComments(filePath);
        if (res && res.status === 200 && Array.isArray(res.data)) existing = res.data;
    } catch {
        existing = [];
    }
    const oldLabels = existing.filter((c) => c && !c.timeFrom && c.uuid);
    for (const lbl of oldLabels) {
        await apiDeleteComment(filePath, lbl.uuid);
    }
    const trimmed = (text || '').trim();
    if (!trimmed) return; // blank clears the label
    // No timeFrom/timeTo: that absence is exactly what marks this as a label.
    await apiAddComment(filePath, { text: trimmed, author: getAuthUser() });
};

export const apiDeleteFile = (filePath) => {
    const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
    return axios.delete(`${API_BASE}/files/${encoded}`);
};

export const apiDownloadFile = async (filePath, onDownloadProgress) =>
    {
        const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
        const url = `${API_BASE}/files/${encoded}`;
        return axios.get(url, { responseType: 'blob', onDownloadProgress });
    };

export const apiUploadFile = async (filePath, file, hidden, overwrite) =>
    {
        const encoded = filePath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
        const url = `${API_BASE}/files/${encoded}?hidden=${hidden}&overwrite=${overwrite}`;
        
        console.log('API: Uploading file to URL:', url);
        console.log('API: Data type:', typeof file);
        console.log('API: Data size:', file.length || file.size || 'unknown');
        console.log('API: Data constructor:', file.constructor.name);
        
        // Try using fetch instead of axios to avoid any axios transformations
        const response = await fetch(url, {
            method: 'PUT',
            body: file,
            headers: authHeaders({
                'Content-Type': 'application/octet-stream',
            }),
        });
        
        console.log('API: Response status:', response.status);
        
        // Return axios-like response object for compatibility
        return {
            status: response.status,
            statusText: response.statusText,
            data: await response.text()
        };
    };

// ---------------------------------------------------------------------------
// Sharing (PocketBase `device_shares` / `file_shares` collections)
// ---------------------------------------------------------------------------
// Sharing is pure metadata: a grant record names the granter (sharer), the
// grantee, the device, and — for a single-file share — the file name. Because a
// recording's storage path always begins with its owner's user id, the REST
// service reconstructs the covered path as "<sharer>/<device>/[fileName]" and
// authorizes reads against it. Creation/revocation go straight to PocketBase,
// guarded by the collections' API rules (create only as yourself for a device
// you own; delete only your own grants).
const DEVICE_SHARES = 'device_shares';
const FILE_SHARES = 'file_shares';

const pbJsonHeaders = () => authHeaders({ 'Content-Type': 'application/json' });

// Resolve a user record from an email address so it can be used as a grantee.
// Requires the users collection to allow authenticated callers to filter by
// email; returns null when not found or not permitted.
export const findUserByEmail = async (email) => {
    const filter = encodeURIComponent(`email='${email}'`);
    const response = await fetch(
        `${AUTH_BASE}/api/collections/users/records?perPage=1&filter=${filter}`,
        { headers: authHeaders() });
    if (!response.ok) return null;
    const data = await response.json();
    return (data.items && data.items[0]) || null;
};

// List the grant rows the caller is party to (as granter or grantee), with the
// sharer/grantee/device relations expanded for display. The collections' list
// rule already restricts the result to the caller's own shares.
const fetchShares = async (collection) => {
    const response = await fetch(
        `${AUTH_BASE}/api/collections/${collection}/records?perPage=500&expand=sharer,grantee,device`,
        { headers: authHeaders() });
    if (!response.ok) return [];
    const data = await response.json();
    return data.items || [];
};
export const fetchDeviceShares = () => fetchShares(DEVICE_SHARES);
export const fetchFileShares = () => fetchShares(FILE_SHARES);

const createShare = async (collection, body) => {
    const response = await fetch(`${AUTH_BASE}/api/collections/${collection}/records`, {
        method: 'POST',
        headers: pbJsonHeaders(),
        body: JSON.stringify({ sharer: getAuthUserId(), ...body }),
    });
    if (!response.ok) {
        let message = `Share failed (${response.status})`;
        try { const err = await response.json(); if (err?.message) message = err.message; } catch { /* ignore */ }
        throw new Error(message);
    }
    return response.json();
};

// Share every recording of a device (current and future) with another user.
// `extra` denormalizes the device identity onto the record (deviceUsername +
// deviceName) so the grantee — who cannot read the devices collection — can
// still show the shared device with its live status and friendly name.
export const createDeviceShare = (deviceRecordId, granteeId, extra = {}) =>
    createShare(DEVICE_SHARES, { grantee: granteeId, device: deviceRecordId, ...extra });

// Share a single recording. deviceRecordId is the device folder (the blob
// path's second segment); fileName is the blob's file name. `extra` may carry
// deviceName so the shared file's device folder is labeled for the grantee.
export const createFileShare = (deviceRecordId, fileName, granteeId, extra = {}) =>
    createShare(FILE_SHARES, { grantee: granteeId, device: deviceRecordId, fileName, ...extra });

// Revoke a grant (only the granter may do this; enforced by the delete rule).
export const deleteDeviceShare = (id) =>
    fetch(`${AUTH_BASE}/api/collections/${DEVICE_SHARES}/records/${id}`, { method: 'DELETE', headers: authHeaders() });
export const deleteFileShare = (id) =>
    fetch(`${AUTH_BASE}/api/collections/${FILE_SHARES}/records/${id}`, { method: 'DELETE', headers: authHeaders() });

// Shares RECEIVED by the current user (as grantee), used to inline shared
// content into the Devices / Streams tabs. Each row carries the denormalized
// deviceUsername/deviceName.
export const fetchSharedWithMe = async () => {
    const myId = getAuthUserId();
    const [ds, fs] = await Promise.all([fetchDeviceShares(), fetchFileShares()]);
    return {
        deviceShares: (ds || []).filter((r) => r.grantee === myId),
        fileShares: (fs || []).filter((r) => r.grantee === myId),
    };
};

// Resolve received shares into blob-listing items (same shape as fetchFiles'
// items: {name, path, size, lastModified, metadata, type}) so they merge
// straight into the Streams file tree. A device share is expanded by listing
// its "<sharer>/<device>/" prefix; a file share is resolved individually via
// properties (listing that directory is intentionally not authorized, to avoid
// exposing un-shared siblings). Every item is tagged shared:true.
export const fetchSharedFiles = async () => {
    const { deviceShares, fileShares } = await fetchSharedWithMe();

    // Resolve each share in parallel but keep its items at the SAME index as its
    // share row. Promise.all preserves result order regardless of which request
    // finishes first, so the merged list is stable across polls. (Pushing from
    // each async callback appended in completion order, which reshuffled the list
    // every 5 s refresh as per-device network timings varied.)
    const deviceItemGroups = await Promise.all((deviceShares || []).map(async (row) => {
        try {
            const res = await fetchFiles(`${row.sharer}/${row.device}/`);
            return (res.data || []).map((b) => ({ ...b, shared: true, sharer: row.sharer }));
        } catch { /* a revoked/missing share just yields nothing */ return []; }
    }));

    const fileItemGroups = await Promise.all((fileShares || []).map(async (row) => {
        const dir = `${row.sharer}/${row.device}/`;
        try {
            const res = await fetchFileProperties(dir + row.fileName);
            const p = res.data || {};
            return [{
                name: row.fileName,
                path: dir,
                size: p.size || 0,
                lastModified: p.lastModified || '',
                metadata: p.metadata || {},
                type: 'file',
                shared: true,
                sharer: row.sharer,
            }];
        } catch { /* ignore unresolved file share */ return []; }
    }));

    return [...deviceItemGroups, ...fileItemGroups].flat();
};